<script>
import Alerts from './Alerts.vue'
import FormUpload from '@/components/FileUpload.vue'

export default {
    name: 'FileUpload',
    components: { FormUpload, Alerts },
    props: ['code'],

    data() {
        return {
            axios: this.$http,
            base_url: '',
            erp: { budgeting:[] },

            documents: [],
        }
    },
    mounted() {
        this.loadErp()
        this.loadDocuments()

    },
    computed: {
        groupedDocuments() {
            const groups = {}

            // Grouping logic
            this.documents.forEach(doc => {
                const category = doc.category || 'No Category'
                if (!groups[category]) {
                    groups[category] = {
                        documents: [],
                        totalAmount: 0
                    }
                }
                groups[category].documents.push(doc)
                groups[category].totalAmount += Number(doc.amount || 0)
            })

            // Convert to entries, sort by document count
            const sortedEntries = Object.entries(groups)
                .sort((a, b) => b[1].documents.length - a[1].documents.length)

            // Rebuild sorted object (optional but keeps compatibility)
            const sortedGroups = {}
            sortedEntries.forEach(([key, value]) => {
                sortedGroups[key] = value
            })

            return sortedGroups
        },
        getReceiptsSummary() {
            const summary = {}

            this.documents.forEach(doc => {
                const category = doc.category || 'No Category'
                const amount = parseFloat(doc.amount) || 0

                if (!summary[category]) {
                    summary[category] = {
                        total: 0,
                        count: 0
                    }
                }

                summary[category].total += amount
                summary[category].count += 1
            })

            return summary
        }
    },

    methods: {
        loadErp() {
            this.axios.get(`${this.base_url}/request/${this.code}`).then(response => {
                this.erp = response.data.data
            })
        },
        getBudgetTitle(code) {
            const match = this.erp.budgeting.find(a => a.code === code);
            if (match) {
                return match.item;
            }
            return 'No Category'
        },
        loadDocuments() {
            this.wait = true
            this.clear()
            this.axios.get(`${this.base_url}/request/${this.code}/docs`).then(response => {
                this.documents = response.data.data
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        },

        deleteDoc(doc) {
            this.wait = true
            if (confirm(`Delete this document? ${doc.title}`)) {
                this.axios.post(`${this.base_url}/request/${this.code}/docs`, {
                    delete_id: doc.id
                }).then(() => {
                    this.loadDocuments()
                }).catch(error => {
                    this.showError(error)
                }).then(() => {
                    this.wait = false
                })
            }
        }
    }
}
</script>

<template>
    <div class="p-4 bg-white shadow-sm rounded-lg mb-3">
        <div v-if="erp.code" class="mb-3">
            <h5 class="font-weight-bold">CODE: {{ erp.code }} | {{ erp.currency }} <span class="font">{{ money(erp.amount) }}</span></h5>
            {{ erp.title }}
        </div>

        <!-- Upload section -->
        <br>
        <FormUpload :code="code" @reloadDocuments="loadDocuments"></FormUpload>

        <!-- Receipts summary -->
        <h5 class="mt-4 font-weight-bold">Receipt Summary</h5>
        <table class="table table-sm table-bordered bg-white">
            <thead class="thead-light">
            <tr>
                <th>Category</th>
                <th># of Receipts</th>
                <th class="text-right">Total Amount</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(data, category) in getReceiptsSummary" :key="category">
                <td>{{ getBudgetTitle(category) }}</td>
                <td>{{ data.count }}</td>
                <td class="text-right text-success font-weight-bold">
                    {{ Number(data.total).toLocaleString() }} /=
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <th colspan="2" class="text-right">Grand Total</th>
                <th class="text-right font-weight-bold">
                    <u>
                        {{
                            Object.values(getReceiptsSummary)
                                .reduce((sum, cat) => sum + cat.total, 0)
                                .toLocaleString()
                        }} /=
                    </u>
                </th>
            </tr>
            </tfoot>
        </table>

        <!-- Uploaded documents -->
        <section>
            <!-- Section Heading -->
            <h5 class="font-weight-bold">
                Documents <span class="text-muted">({{ documents.length }})</span>
            </h5>

            <div class="accordion mt-4" id="documentsAccordion">
                <div v-for="(group, category, index) in groupedDocuments" :key="category" class="card">
                    <!-- Accordion Header -->
                    <div class="card-header d-flex justify-content-between align-items-center" :id="'heading' + index">
                        <h5 class="mb-0">
                            <a href="#"
                               class="btn-link"
                               type="button"
                               data-toggle="collapse"
                               :data-target="'#collapse' + index"
                               aria-expanded="true"
                               :aria-controls="'collapse' + index">
                                {{ getBudgetTitle(category) }}
                            </a>
                        </h5>
                        <span class="text-muted">
                              Total:
                              <span :class="group.totalAmount > 0 ? 'text-success font-weight-bold' : 'text-muted'">
                                {{ Number(group.totalAmount || 0).toLocaleString() }} /=
                              </span>
                            </span>
                    </div>

                    <!-- Accordion Body -->
                    <div
                        :id="'collapse' + index"
                        class="collapse"
                        :aria-labelledby="'heading' + index"
                        data-parent="#documentsAccordion"
                    >
                        <div class="card-body">
                            <div class="row">
                                <div
                                    v-for="doc in group.documents"
                                    :key="doc.id"
                                    class="col-md-6 mb-4">
                                    <div class="shadow-sm rounded-lg d-flex flex-column h-100 border">
                                        <!-- Preview -->
                                        <div class="flex-fill d-flex flex-column">
                                            <div class="flex-fill">
                                                <a :href="doc.url" target="_blank">
                                                    <img
                                                        v-if="doc.type === 'img'"
                                                        :src="doc.url"
                                                        class="img-fluid rounded-top"
                                                        style="max-height: 300px; object-fit: contain;"
                                                        alt="Image Preview"
                                                    />
                                                    <embed
                                                        v-else-if="doc.type === 'pdf'"
                                                        :src="doc.url"
                                                        type="application/pdf"
                                                        width="100%"
                                                        style="min-height: 280px;"
                                                    />
                                                    <div v-else class="text-center text-muted py-5">
                                                        No Preview Available
                                                    </div>
                                                </a>
                                            </div>

                                            <!-- Metadata -->
                                            <div class="bg-light p-3 small">
                                                <h6 class="mb-2 font-weight-bold">
                                                    <a :href="doc.url" target="_blank">{{ doc.title }}</a>
                                                </h6>

                                                <div>
                                                    <strong>Uploaded:</strong>
                                                    {{ day(doc.created_at).format('MMM DD, YYYY') }}
                                                </div>

                                                <div>
                                                    <strong>Amount:</strong>
                                                    <span
                                                        :class="doc.amount > 0 ? 'text-success font-weight-bold h6' : 'text-muted'"
                                                    >
                                                    {{ doc.amount ? Number(doc.amount).toLocaleString() : '0' }} /=
                                                  </span>
                                                </div>

                                                <div>
                                                    <strong>Path:</strong>
                                                    <span class="text-truncate" style="max-width: 100%;"
                                                          :title="doc.path">
                                                        <small class="text-muted">{{ doc.path }}</small>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Actions -->
                                        <div class="p-2 border-top text-right">
                                            <!-- <button
                                                v-if="!doc.system_doc"
                                                :disabled="wait"
                                                class="btn btn-outline-dark btn-sm"
                                                @click="editDocument(doc)">
                                                Edit
                                            </button> -->
                                            <button
                                                v-if="!doc.system_doc"
                                                :disabled="wait"
                                                class="btn btn-outline-danger btn-sm"
                                                @click="deleteDoc(doc)">
                                                Delete
                                            </button>
                                            <span v-else class="badge badge-secondary">System Generated File</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> <!-- end collapse -->
                </div> <!-- end card -->
            </div> <!-- end accordion -->
        </section>

    </div>
</template>

<style scoped>
.row.g-1 {
    margin-left: -0.5rem; /* Adjust the negative margin to reduce the gap */
    margin-right: -0.5rem;
}

</style>
